import { get, size } from 'lodash/fp';
import { createSelector } from 'reselect';
import { pipe } from 'fp-ts/function';
import { BillState } from '@mews-commander/data-access';
import { ApplicationState, AsyncAction, CommanderThunkAction, WithAccountId, WithScope } from '@mews-commander/types';
import {
    createAction,
    createAsyncActionTypes,
    createAsyncMetaSelectors,
    createScopedReducer,
    createScopedSelector,
    lt,
    showSuccessAlert,
} from '@mews/framework';
import { compactArray, primitiveTypeUniq } from '@mews/fp';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { getPaymentIdsByBillIds, getPaymentsByIds } from '@mews-commander/legacy-entities/src/ducks/payments';
import {
    DEFAULT_BILL_FETCH_EXTENT,
    addBill,
    fetchCurrentEnterpriseBillsByAccountIds,
} from '@mews-commander/legacy-entities/src/actions/bills';
import { fetchAccountById } from '@mews-commander/legacy-entities/src/actions/accounts';
import { META_STATE_SUCCEEDED } from '@mews-commander/legacy-entities/src/constants';
import { getBill, getUniqueBillOwnerIdsByBillIds } from '@mews-commander/legacy-entities/src/ducks/bills';
import { getOrderItemIdsByBillId, getOrderItemsByIds } from '@mews-commander/legacy-entities/src/ducks/orderItems';
import { createEntityAsyncMetaHandlers } from '@mews-commander/legacy-entities/src/ducks/utils';
import { logBillingAction } from '../utils/logBillingAction';
import { moduleSelector as parentSelector, prefix } from '../module';
import { moveItemsToBill } from '../actions/bills';
import { openMoveItemsReviewDialog } from '../BillUpdatedReviewDialog/actions';
import { deselectAll, getSelectedOrderItemIds, getSelectedPaymentIds } from './billItems';

export const NAME = 'itemsAssignment';

const BILLING_EXTENT = {
    orderItems: true,
    payments: true,
};

const ITEM_ASSIGNMENT_DIALOG_FETCH = prefix('ITEM_ASSIGNMENT_DIALOG_FETCH');

const [
    ITEM_ASSIGNMENT_DIALOG_FETCH_STARTED,
    ITEM_ASSIGNMENT_DIALOG_FETCH_SUCCEEDED,
    ITEM_ASSIGNMENT_DIALOG_FETCH_FAILED,
] = createAsyncActionTypes(ITEM_ASSIGNMENT_DIALOG_FETCH);
const ITEM_ASSIGNMENT_DIALOG_OPENED = prefix('ITEM_ASSIGNMENT_DIALOG_OPENED');
const ITEM_ASSIGNMENT_DIALOG_CLOSED = prefix('ITEM_ASSIGNMENT_DIALOG_CLOSED');
const ITEM_ASSIGNMENT_DIALOG_ACCOUNT_SELECTED = prefix('ITEM_ASSIGNMENT_DIALOG_ACCOUNT_SELECTED');
const ITEM_ASSIGNMENT_DIALOG_ACCOUNT_DESELECTED = prefix('ITEM_ASSIGNMENT_DIALOG_ACCOUNT_DESELECTED');

export const getMoveableItemsIds = (state: ApplicationState, { scope }: WithScope) => {
    const enterpriseId = getCurrentEnterpriseId();
    const sourceBillId = getItemAssignmentDialogBillId(state, { scope });
    const itemAssignmentOrderItemIds = getItemAssignmentOrderItemIds(state, { scope });

    if (sourceBillId) {
        return {
            paymentIds: getPaymentIdsByBillIds(state, { billIds: [sourceBillId], enterpriseId }),
            orderItemsIds: itemAssignmentOrderItemIds || getOrderItemIdsByBillId(state, { billId: sourceBillId }),
        };
    }

    return {
        paymentIds: getSelectedPaymentIds(state, { scope }),
        orderItemsIds: itemAssignmentOrderItemIds || getSelectedOrderItemIds(state, { scope }),
    };
};

interface MoveSelectedItemsToBillProps extends WithScope {
    targetBillId: string;
    associatedAccountId?: string;
    associateCurrentOwner?: boolean;
    deleteEmptyBills?: (targetBill?: string) => void;
}

export const moveSelectedItemsToBill =
    ({ targetBillId, scope, associatedAccountId }: MoveSelectedItemsToBillProps): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState) => {
        const state = getState();

        const { ownerId: accountId } = getBill(state, { id: targetBillId }) || {};
        const { paymentIds, orderItemsIds } = getMoveableItemsIds(state, { scope });

        const promise = dispatch(
            moveItemsToBill({ associatedAccountId, targetBillId, accountId, paymentIds, orderItemsIds, scope })
        );

        dispatch(
            logBillingAction('bill items moved to existing bill', {
                paymentCount: size(paymentIds),
                orderItemCount: size(orderItemsIds),
            })
        );
        return dispatch(createAction(ITEM_ASSIGNMENT_DIALOG_FETCH, promise, { scope }));
    };

export const moveItemsCloseDialog =
    ({
        targetBillId,
        scope,
        associateCurrentOwner,
        deleteEmptyBills,
    }: MoveSelectedItemsToBillProps): CommanderThunkAction =>
    async (dispatch, getState) => {
        const itemBillIds = getSelectedItemsUniqueBillIds(getState(), scope);
        const billOwnerId = associateCurrentOwner
            ? getUniqueBillOwnerIdsByBillIds(getState(), { ids: itemBillIds })?.[0]
            : undefined;

        const targetBill = getBill(getState(), { id: targetBillId });

        const associatedAccountId =
            associateCurrentOwner && billOwnerId !== targetBill?.ownerId ? billOwnerId : undefined;

        return dispatch(moveSelectedItemsToBill({ targetBillId, scope, associatedAccountId })).then(() => {
            if (deleteEmptyBills) deleteEmptyBills(targetBillId);
            dispatch(openMoveItemsReviewDialog({ fromBillIds: itemBillIds, toBillId: targetBillId }));
            dispatch(closeItemAssignmentDialog(scope));
            dispatch(deselectAll(scope));
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
        });
    };

interface MoveSelectedItemsToNewBillProps extends WithScope, WithAccountId {
    name?: string;
    paymentIds?: string[];
    orderItemsIds?: string[];
    associatedAccountId?: string;
    associateCurrentOwner?: boolean;
    deleteEmptyBills?: () => void;
}

export const moveSelectedItemsToNewBill =
    ({
        accountId,
        name,
        scope,
        paymentIds = [],
        orderItemsIds = [],
        associatedAccountId,
    }: MoveSelectedItemsToNewBillProps): CommanderThunkAction<AsyncAction> =>
    async (dispatch) => {
        const promise = dispatch(addBill({ accountId, name, associatedAccountId })).then(({ value: targetBillId }) =>
            dispatch(moveItemsToBill({ targetBillId, accountId, paymentIds, orderItemsIds, scope })).then(() => {
                dispatch(
                    logBillingAction('bill items moved to new bill', {
                        paymentCount: size(paymentIds),
                        orderItemCount: size(orderItemsIds),
                    })
                );

                return targetBillId;
            })
        );

        return dispatch(createAction(ITEM_ASSIGNMENT_DIALOG_FETCH, promise, { scope }));
    };

export const moveSelectedItemsToNewBillCloseDialog =
    ({
        accountId,
        scope,
        associateCurrentOwner,
        deleteEmptyBills,
        name,
    }: MoveSelectedItemsToNewBillProps): CommanderThunkAction<Promise<void>> =>
    async (dispatch, getState) => {
        const { paymentIds, orderItemsIds } = getMoveableItemsIds(getState(), { scope });
        const itemBillIds = getSelectedItemsUniqueBillIds(getState(), scope);
        const billOwnerId = associateCurrentOwner
            ? getUniqueBillOwnerIdsByBillIds(getState(), { ids: itemBillIds })?.[0]
            : undefined;
        const associatedAccountId = associateCurrentOwner && billOwnerId !== accountId ? billOwnerId : undefined;

        await dispatch(
            moveSelectedItemsToNewBill({ accountId, name, scope, paymentIds, orderItemsIds, associatedAccountId })
        ).then(({ value: targetBillId }) => {
            dispatch(
                openMoveItemsReviewDialog({
                    fromBillIds: itemBillIds,
                    toBillId: targetBillId,
                })
            );
            dispatch(deselectAll(scope));
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
            if (deleteEmptyBills) deleteEmptyBills();
        });
    };

interface OpenItemAssignmentDialogProps {
    billId: string | null;
    scope: string;
    orderItemIds?: string[];
}

export const openItemAssignmentDialog = ({ billId, scope, orderItemIds }: OpenItemAssignmentDialogProps) =>
    createAction(ITEM_ASSIGNMENT_DIALOG_OPENED, { billId, orderItemIds }, { scope });

export const closeItemAssignmentDialog = (scope: string) =>
    createAction(ITEM_ASSIGNMENT_DIALOG_CLOSED, null, { scope });

export const selectItemAssignmentAccount =
    ({ accountId, scope }: $TSFixMe) =>
    (dispatch: $TSFixMe) => {
        const accountPromise = dispatch(fetchAccountById({ accountId, extent: {} }));
        const billsPromise = dispatch(
            fetchCurrentEnterpriseBillsByAccountIds({
                accountIds: [accountId],
                extent: { ...DEFAULT_BILL_FETCH_EXTENT, ...BILLING_EXTENT },
                state: BillState.Open,
            })
        );

        const promise = Promise.all([accountPromise, billsPromise]).then(() =>
            dispatch(createAction(ITEM_ASSIGNMENT_DIALOG_ACCOUNT_SELECTED, accountId, { scope }))
        );

        return dispatch(createAction(ITEM_ASSIGNMENT_DIALOG_FETCH, promise, { scope }));
    };

export const clearItemAssignmentAccount = (scope: $TSFixMe) =>
    createAction(ITEM_ASSIGNMENT_DIALOG_ACCOUNT_DESELECTED, null, { scope });

const INITIAL_STATE = {
    itemAssignmentDialogOpened: false,
    itemAssignmentBillId: null,
    itemAssignmentAccountId: null,
    itemAssignmentOrderItemIds: null,
    ...META_STATE_SUCCEEDED,
};

export const itemAssignmentReducer = createScopedReducer(INITIAL_STATE, {
    // @ts-expect-error $TSFixMeError: Parameter 'state' implicitly has an 'any' type.
    [ITEM_ASSIGNMENT_DIALOG_OPENED]: (state, { billId, orderItemIds }) => ({
        ...state,
        itemAssignmentDialogOpened: true,
        itemAssignmentBillId: billId,
        itemAssignmentOrderItemIds: orderItemIds,
    }),
    [ITEM_ASSIGNMENT_DIALOG_CLOSED]: (state: $TSFixMe) => ({
        ...state,
        itemAssignmentDialogOpened: false,
        itemAssignmentBillId: null,
        itemAssignmentOrderItemIds: null,
    }),
    // @ts-expect-error $TSFixMeError: Parameter 'state' implicitly has an 'any' type.
    [ITEM_ASSIGNMENT_DIALOG_ACCOUNT_SELECTED]: (state, payload) => ({
        ...state,
        itemAssignmentAccountId: payload,
    }),
    [ITEM_ASSIGNMENT_DIALOG_ACCOUNT_DESELECTED]: (state: $TSFixMe) => ({
        ...state,
        itemAssignmentAccountId: null,
    }),
    ...createEntityAsyncMetaHandlers({
        STARTED: ITEM_ASSIGNMENT_DIALOG_FETCH_STARTED,
        SUCCEEDED: ITEM_ASSIGNMENT_DIALOG_FETCH_SUCCEEDED,
        FAILED: ITEM_ASSIGNMENT_DIALOG_FETCH_FAILED,
    }),
});

const moduleSelector = createSelector(parentSelector, get(NAME));

const getScope = createScopedSelector({ parentSelector: moduleSelector, initialState: INITIAL_STATE });

const getMeta = createSelector(getScope, get('meta'));

export const getItemAssignmentDialogBillId = createSelector(getScope, get('itemAssignmentBillId'));

export const getItemAssignmentOrderItemIds = createSelector(getScope, get('itemAssignmentOrderItemIds'));

export const [isFetching, hasFailed] = createAsyncMetaSelectors(getMeta);

export const isItemAssignmentDialogOpened = createSelector(getScope, get('itemAssignmentDialogOpened'));

export const getItemAssignmentDialogAccountId = createSelector(getScope, get('itemAssignmentAccountId'));

export const getSelectedItemsUniqueBillIds = (state: ApplicationState, scope: string) => {
    const { paymentIds, orderItemsIds } = getMoveableItemsIds(state, { scope });
    const enterpriseId = getCurrentEnterpriseId();
    const payments = getPaymentsByIds(state, { ids: paymentIds, enterpriseId });
    const orderItems = getOrderItemsByIds(state, { ids: orderItemsIds });

    return pipe(
        [...payments.map((payment) => payment.billId), ...orderItems.map((orderItem) => orderItem.billId)],
        compactArray,
        primitiveTypeUniq
    );
};

export const canAssociateCurrentOwner = ({
    uniqueBillOwners,
    targetAccountId,
}: {
    uniqueBillOwners: string[];
    targetAccountId: string;
}) => uniqueBillOwners.length === 1 && targetAccountId !== uniqueBillOwners[0];
